import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import Slider from 'react-slick'
import { HACKLE_FLAG_KEY, useFeatureFlag } from '../../../../helpers/hackleHelper'
import apis from '../../../../modules/apis'
import { axiosGet } from '../../../../modules/axiosHelper'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import { n_common, n_review } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import urls from '../../../../modules/urls'
import useEvaluationStore from '../../../../store/useEvaluationStore'
import { Tutor } from './EvaluationModalBody'

export interface IKeywords {
  positive_keywords: TiveKeyword[]
  negative_keywords: TiveKeyword[]
}

export interface TiveKeyword {
  category: Category
  keywords: Category[]
}

export interface Category {
  id: number
  title: string
}

interface ICategoryKeywords {
  data: TiveKeyword
  keywordsList: number[]
  handleClickKeyword: (id: number) => void
}
const KeywordBox = ({ handleClickKeyword, keywordsList, item }) => {
  const [hover, setHover] = useState<boolean>(false)

  return (
    <div className="w-full">
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={() => handleClickKeyword(item.id)}
        className={classNames(
          'Body2-14Md inline-block cursor-pointer whitespace-nowrap rounded-[5px] px-[8px] py-[8px] shadow-md',
          { 'bg-purple-500 text-white': keywordsList.includes(item.id) },
          { 'bg-purple-50 text-purple-500': !keywordsList.includes(item.id) && hover },
          { 'bg-gray-50 text-black': !keywordsList.includes(item.id) && !hover }
        )}
      >
        {item.title}
      </div>
    </div>
  )
}

const CategoryKeywords = ({ data, keywordsList, handleClickKeyword }: ICategoryKeywords) => {
  const [shuffledKeywordList, setShuffledKeywordList] = useState([])

  useEffect(() => {
    if (data.keywords) {
      setShuffledKeywordList(shuffleArray(data.keywords))
    }
  }, [])

  return (
    <div className="mr-[12px] min-w-[0px] pb-[8px]">
      <div className="Body2-14Bd mb-[8px]">{data.category.title}</div>
      <div className="flex flex-col gap-[8px] text-[16px]">
        {shuffledKeywordList?.map((item, i) => {
          return <KeywordBox key={i} handleClickKeyword={handleClickKeyword} item={item} keywordsList={keywordsList} />
        })}
      </div>
    </div>
  )
}

const shuffleArray = (array) => {
  const arr = array.slice()
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }

  return arr
}

interface ILessonEvaluationStep2 {
  next: () => void
  tutorData: Tutor
  lessonId: number | string
  sendClickEventToGTM: (buttonName: string) => void
  onClose: () => void
}
const EvaluationStep2 = ({ next, tutorData, sendClickEventToGTM, onClose, lessonId }: ILessonEvaluationStep2) => {
  const { t } = useTranslation()

  const router = useAppRouter()
  const isClassroom = router?.asPath?.includes('/lessons/classroom/') ?? false
  const moveLessonCompletePage = useFeatureFlag(HACKLE_FLAG_KEY.MOVE_LESSON_COMPLETE_PAGE)

  const { keywordsList, setKeywordsList } = useEvaluationStore((state) => ({
    keywordsList: state.keywordsList,
    setKeywordsList: state.setKeywordsList,
  }))
  const evaluationRate = useEvaluationStore((state) => state.evaluationRate)

  const handleClickKeyword = (keywordId: number) => {
    const temp = keywordsList.slice() // 기존의 배열을 복사하여 새로운 배열을 만듭니다.

    if (temp.includes(keywordId)) {
      // 복사한 배열에서 검색
      temp.splice(temp.indexOf(keywordId), 1) // 복사한 배열에서 클릭한 키워드를 제거합니다.
    } else {
      temp.push(keywordId) // 클릭한 키워드를 배열에 추가합니다.
    }

    setKeywordsList(temp)
  }

  const handleLaterButton = () => {
    onClose()
    sendClickEventToGTM('나중에 하기')

    if (isClassroom) {
      if (moveLessonCompletePage) {
        router.push(urls.student.newPortal.lessons.complete + '?lesson_id=' + lessonId)
      } else {
        router.push(urls.student.newPortal.lessons.reviews.home)
      }
    }
  }

  const [selectedCell, setSelectedCell] = useState(0)
  const [negativeKeywords, setNegativeKeywords] = useState<TiveKeyword[]>([])
  const [positiveKeywords, setPositiveKeywords] = useState<TiveKeyword[]>([])
  const isGoodRate = evaluationRate >= 4
  const filteredList = isGoodRate ? positiveKeywords : negativeKeywords
  const [shuffledList, setShuffledList] = useState([])
  const isAvailableNext = keywordsList && keywordsList.length > 0

  useEffect(() => {
    if (filteredList) {
      setShuffledList(shuffleArray(filteredList))
    }
  }, [filteredList])

  function customPaging(i: number) {
    return (
      <div className="flex justify-center pl-[8px] pt-[8px] sm:pl-[0px] sm:pt-[0px]">
        <div
          onClick={() => setSelectedCell(i)}
          className={classNames(
            'mx-[2px] h-[6px] rounded-full',
            {
              'w-[16px] bg-purple-500': selectedCell === i,
            },
            {
              'w-[6px] bg-gray-200': selectedCell != i,
            }
          )}
        ></div>
      </div>
    )
  }

  const fetchReportCategories = async () => {
    try {
      const response = await axiosGet(apis.evaluation.keywords())
      return response
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchReportCategories()
        setNegativeKeywords(response.negative_keywords)
        setPositiveKeywords(response.positive_keywords)
      } catch (error) {
        console.log(error)
      }
    }
    fetchData()
  }, [])

  const handleNextButton = (isAvailableNext: boolean) => {
    if (isAvailableNext) {
      next()
      sendClickEventToGTM('선택 완료')
    }
  }

  const slider = useRef(null)

  function scroll(e) {
    if (slider === null) return 0
    e.wheelDelta > 0 ? slider.current.slickPrev() : slider.current.slickNext()
  }

  useEffect(() => {
    window.addEventListener('wheel', scroll, true)

    return () => {
      window.removeEventListener('wheel', scroll, true)
    }
  }, [])

  return (
    <div className="flex h-[calc(100vh_-_100px)] flex-col justify-between sm:h-full">
      <div className="Headline2-24Bd  font-medium">
        {t(
          isGoodRate ? n_review.step_2.title_positive(tutorData.name) : n_review.step_2.title_negative(tutorData.name)
        )}
      </div>
      <div className="no-scrollbar mt-[16px] w-auto overflow-x-hidden  overflow-y-hidden">
        <div className=" h-[396px]">
          <Slider
            ref={slider}
            speed={300}
            infinite={false}
            cssEase="linear"
            dots={true}
            slidesToShow={1}
            swipeToSlide={true}
            centerMode={false}
            variableWidth={true}
            className="new-evaluation-slider"
            beforeChange={(current: any, next: any) => setSelectedCell(next)}
            customPaging={customPaging}
          >
            {shuffledList.map((data, i) => {
              return (
                <CategoryKeywords
                  key={i}
                  data={data}
                  keywordsList={keywordsList}
                  handleClickKeyword={handleClickKeyword}
                />
              )
            })}
          </Slider>
        </div>
      </div>
      <div className="mt-[-2px] flex justify-end gap-[8px]">
        <div className="hidden sm:block sm:h-[46px] sm:w-[112px]">
          <DSButton
            disabled={!isAvailableNext}
            size={BUTTON_SIZE.MD}
            type={BUTTON_TYPE.OUTLINE}
            onClick={() => handleLaterButton()}
          >
            <DSButton.Label>{t(n_common.btn_leave)}</DSButton.Label>
          </DSButton>
        </div>
        <div className="mb-[34px] w-full sm:mb-[0px] sm:h-[46px] sm:w-[112px]">
          <DSButton
            disabled={!isAvailableNext}
            size={BUTTON_SIZE.MD}
            type={BUTTON_TYPE.SOLID}
            onClick={() => handleNextButton(isAvailableNext)}
            className="border-1 border-transparent"
          >
            <DSButton.Label>{t(n_common.btn_next)}</DSButton.Label>
          </DSButton>
        </div>
      </div>
    </div>
  )
}

export default EvaluationStep2
